import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkflowAnimatedDemoId } from './workflowDemoConstants';

const WhatsAppWorkflowDemo = lazy(() =>
	import('./WhatsAppWorkflowDemo').then((module) => ({
		default: module.WhatsAppWorkflowDemo,
	})),
);

const CallProntuarioWorkflowDemo = lazy(() =>
	import('./CallProntuarioWorkflowDemo').then((module) => ({
		default: module.CallProntuarioWorkflowDemo,
	})),
);

const EvolucaoProntuarioWorkflowDemo = lazy(() =>
	import('./EvolucaoProntuarioWorkflowDemo').then((module) => ({
		default: module.EvolucaoProntuarioWorkflowDemo,
	})),
);

const ProntuarioWorkflowDemo = lazy(() =>
	import('./ProntuarioWorkflowDemo').then((module) => ({
		default: module.ProntuarioWorkflowDemo,
	})),
);

type WorkflowDemoPlayerProps = {
	demoId: WorkflowAnimatedDemoId;
	alt: string;
	expanded?: boolean;
	onExpand?: () => void;
	className?: string;
};

const WorkflowDemoFallback = () => (
	<div className="flex size-full items-center justify-center bg-gradient-to-br from-brand-orange/5 to-brand-pink/5">
		<div className="size-8 animate-spin rounded-full border-2 border-brand-orange/20 border-t-brand-orange" />
	</div>
);

const WorkflowDemoScene = ({
	demoId,
	playing,
}: {
	demoId: WorkflowAnimatedDemoId;
	playing: boolean;
}) => {
	switch (demoId) {
		case 'agendamentos':
			return <WhatsAppWorkflowDemo playing={playing} />;
		case 'chamada':
			return <CallProntuarioWorkflowDemo playing={playing} />;
		case 'evolucao':
			return <EvolucaoProntuarioWorkflowDemo playing={playing} />;
		case 'prontuario':
			return <ProntuarioWorkflowDemo playing={playing} />;
		default:
			return null;
	}
};

const WorkflowDemoPlayer = ({
	demoId,
	alt,
	expanded = false,
	onExpand,
	className,
}: WorkflowDemoPlayerProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const node = containerRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsVisible(entry?.isIntersecting ?? false);
			},
			{ threshold: 0.35 },
		);

		observer.observe(node);

		return () => observer.disconnect();
	}, []);

	const playing = expanded || isVisible;

	const content = (
		<Suspense fallback={<WorkflowDemoFallback />}>
			<WorkflowDemoScene demoId={demoId} playing={playing} />
		</Suspense>
	);

	if (expanded) {
		return (
			<div
				className={cn(
					'aspect-[16/10] w-full overflow-hidden rounded-2xl bg-white shadow-2xl',
					className,
				)}
				aria-label={alt}
			>
				{content}
			</div>
		);
	}

	return (
		<div
			ref={containerRef}
			className={cn(
				'relative aspect-[16/10] w-full overflow-hidden rounded-2xl border border-brand-orange/10 bg-white shadow-lg shadow-brand-orange/5',
				className,
			)}
		>
			{onExpand ? (
				<button
					type="button"
					onClick={onExpand}
					aria-label={`Ampliar demonstração: ${alt}`}
					className="absolute inset-0 z-10 cursor-pointer border-0 bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2"
				>
					<span className="sr-only">{alt}</span>
				</button>
			) : null}

			<div aria-hidden className="size-full">
				{content}
			</div>

			{onExpand ? (
				<div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/35 group-hover:opacity-100 group-focus-within:bg-black/35 group-focus-within:opacity-100">
					<Maximize2
						className="h-10 w-10 text-white drop-shadow"
						strokeWidth={2.25}
						aria-hidden
					/>
				</div>
			) : null}
		</div>
	);
};

export { WorkflowDemoPlayer };
